import { actions } from "@/actions";
import { CollectionItemSubtitleContainer } from "@/components/collection-item-subtitle/collection-item-subtitle-container";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet";
import { ItemPreviewState } from "@/components/item-preview/item-preview-state";
import { ItemListItemRowData, ItemListRowType } from "@/components/item-list/types";
import { ItemListItemKind } from "@/components/item-list/types/item-list-item-types";
import { EventContext } from "@/domains/metrics/context";
import { MDSIconKind, MDSMenuContentList, MDSMenuContentItem, MDSMenuContentItemKind } from "@/mds";
import { itemsModule } from "@/modules/items";
import { AppStore } from "@/store/AppStore";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { CollectionsListPageStore } from "@/store/pages/CollectionsListPageStore";
import { ListStateObservable } from "@/store/pages/ListStateObservable";

export const getItemListRowForCollection = ({
  dateLabel,
  listState,
  collectionObservable,
  store,
  trackClick,
  tabId,
  disablePreview = false,
  collectionsListPageStore,
}: {
  dateLabel?: string;
  listState?: ListStateObservable;
  collectionObservable: CollectionObservable;
  store: AppStore;
  trackClick?: () => void;
  tabId?: string;
  disablePreview?: boolean;
  collectionsListPageStore?: CollectionsListPageStore;
}) => {
  const itemAppearance = itemsModule.resolveAppearance({
    itemId: collectionObservable.id,
    appStore: store,
  });

  const row: ItemListItemRowData = {
    type: ItemListRowType.Item,
    key: collectionObservable.id,
    payload: {
      id: collectionObservable.id,
      kind: ItemListItemKind.Collection,
      titleIcon: collectionObservable.sharedAt ? { kind: MDSIconKind.Shared } : undefined,
      appearance: itemAppearance,
      createPreviewState: disablePreview
        ? undefined
        : () =>
            new ItemPreviewState({
              store: collectionObservable.store,
              id: collectionObservable.id,
              kind: ItemListItemKind.Collection,
            }),
      label: collectionObservable.label,
      onClick: ({ event }) => {
        store.navigation.goToCollection({
          collectionId: collectionObservable.id,
          event,
          activeTabId: tabId,
        });
        trackClick?.();
      },
      sharedBy: collectionObservable.sharedBy,
      dateLabel,
      dropdown: generateItemDropdown(collectionObservable, collectionsListPageStore),
      listState,
      extraRow: {
        id: "collection-description",
        content: () => <CollectionItemSubtitleContainer collection={collectionObservable} />,
      },
    },
  };
  return row;
};

const generateItemDropdown = (
  collectionObservable: CollectionObservable,
  collectionsListPageStore?: CollectionsListPageStore
): MDSMenuContentList | undefined => {
  if (!collectionsListPageStore) return;

  const ownerActions: MDSMenuContentItem[] = collectionObservable.isOwnedByMe
    ? [
        {
          id: `delete-${collectionObservable.id}`,
          kind: MDSMenuContentItemKind.Item,
          icon: MDSIconKind.Trash,
          label: "Delete",
          onClick: () => {
            collectionsListPageStore.deleteCollectionModal.open({
              collectionIds: [collectionObservable.id],
            });
          },
        },
      ]
    : [];

  return [
    {
      id: `copy-link-${collectionObservable.id}`,
      kind: MDSMenuContentItemKind.Item,
      label: "Copy link",
      onClick: () => {
        actions.copyCollectionLinkToClipboard({ collectionId: collectionObservable.id });
      },
      icon: MDSIconKind.Copy,
    },
    {
      id: `share-${collectionObservable.id}`,
      kind: MDSMenuContentItemKind.Item,
      icon: MDSIconKind.Share,
      label: "Share",
      onClick: () =>
        collectionsListPageStore.shareSheetModal.open({
          id: collectionObservable.id,
          entityKind: ShareSheetEntityKind.Collection,
          eventContext: EventContext.CollectionsRowActions,
        }),
    },
    ...ownerActions,
  ];
};
